import React from "react";
import { useNavigate } from "react-router-dom";
import { ShapeSvg } from "@/components/ShapeSvg";
import { Confetti, Speaker } from "@/components/Bits";

/* Renders any content cell: img | emoji | big text | text | color swatch | shape | emoji group */
export function Cell({ c, size = "md" }) {
  const dim = size === "sm" ? "w-12 h-12 text-4xl" : "w-16 h-16 sm:w-24 sm:h-24 text-5xl sm:text-6xl";
  if (c.img) return <img src={c.img} alt={c.label || ""} className={`${dim} object-contain`} onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = "block"); }} />;
  if (c.color) return <div className={`${size === "sm" ? "w-12 h-12" : "w-20 h-20 sm:w-24 sm:h-24"} rounded-3xl shadow-inner border-4 border-white`} style={{ background: c.color, boxShadow: `0 8px 0 0 rgba(0,0,0,0.12), inset 0 -6px 10px rgba(0,0,0,0.12)` }} />;
  if (c.shape) return <ShapeSvg shape={c.shape} size={size === "sm" ? 48 : 88} />;
  if (c.group) return (
    <div className="flex flex-wrap items-center justify-center gap-1 max-w-[180px]">
      {Array.from({ length: c.group.n }).map((_, i) => <span key={i} className="text-2xl sm:text-3xl">{c.group.emoji}</span>)}
    </div>
  );
  if (c.big) return <div className={`font-display font-bold text-purple-700 ${size === "sm" ? "text-4xl" : "text-6xl sm:text-7xl"} leading-none`}>{c.big}</div>;
  if (c.text) return <div className={`font-display font-bold text-purple-800 ${size === "sm" ? "text-lg" : "text-2xl sm:text-3xl"} text-center`}>{c.text}</div>;
  return <div className={size === "sm" ? "text-4xl" : "text-5xl sm:text-6xl"}>{c.emoji || c.label}</div>;
}

/* Option tile with img fallback to emoji */
export function OptionTile({ opt, state, selected, onClick, hideLabel, testId }) {
  const wrongSel = state === "wrong" && selected;
  const rightSel = state === "correct" && selected;
  return (
    <button data-testid={testId} onClick={onClick}
      className={`ll-tile bg-white flex flex-col items-center justify-center p-3 sm:p-4 min-h-[100px] sm:min-h-[130px] ${rightSel ? "ring-4 ring-emerald-400 bg-emerald-50" : ""} ${wrongSel ? "ring-4 ring-pink-300 ll-shake" : ""}`}>
      <Cell c={opt} />
      {opt.img && <div className="text-5xl hidden">{opt.emoji}</div>}
      {!hideLabel && !opt.text && !opt.big && <div className="mt-2 font-bold text-purple-800 text-sm sm:text-base text-center">{opt.label}</div>}
    </button>
  );
}

/* Prompt visual for quiz items */
export function PromptView({ p }) {
  if (!p || p.type === "none") return null;
  const box = "rounded-3xl bg-gradient-to-br from-violet-100 to-purple-200 p-6 flex items-center justify-center min-h-[160px]";
  if (p.type === "letter" || p.type === "bigtext") return <div className={box}><div className="font-display font-bold text-purple-700 text-8xl sm:text-9xl leading-none ll-pop">{p.text}</div></div>;
  if (p.type === "speaker") return (
    <div className={`${box} flex-col gap-3`}>
      <div className="text-purple-600 font-bold">Tap to hear</div>
      <Speaker text={p.text} size={40} testId="prompt-speaker" />
    </div>
  );
  if (p.type === "color") return <div className={box}><div className="w-36 h-36 rounded-[40px] border-8 border-white shadow-xl ll-pop" style={{ background: p.hex }} /></div>;
  if (p.type === "shape") return <div className={box}><ShapeSvg shape={p.shape} size={150} color="#EC4899" className="ll-pop" /></div>;
  if (p.type === "emoji") return <div className={box}><div className="text-8xl ll-pop">{p.emoji}</div></div>;
  if (p.type === "image") return (
    <div className={box}>
      {p.img ? <img src={p.img} alt="" className="w-40 h-40 object-contain ll-pop" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { textContent: p.emoji, className: "text-8xl" })); }} /> : <div className="text-8xl">{p.emoji}</div>}
    </div>
  );
  if (p.type === "group") return (
    <div className={`${box} flex-wrap gap-2 sm:gap-3`} data-testid="count-group">
      {Array.from({ length: p.n }).map((_, i) => <span key={i} className={`${p.small ? "text-3xl sm:text-4xl" : "text-5xl sm:text-6xl"} ll-pop`} style={{ animationDelay: `${i * 50}ms` }}>{p.emoji}</span>)}
    </div>
  );
  if (p.type === "add") return (
    <div className={`${box} gap-3 sm:gap-5`}>
      <div className="flex flex-wrap gap-1 max-w-[140px] justify-center">{Array.from({ length: p.a }).map((_, i) => <span key={i} className="text-4xl">{p.emoji}</span>)}</div>
      <div className="font-display font-bold text-6xl text-purple-700">+</div>
      <div className="flex flex-wrap gap-1 max-w-[140px] justify-center">{Array.from({ length: p.b }).map((_, i) => <span key={i} className="text-4xl">{p.emoji}</span>)}</div>
    </div>
  );
  return null;
}

/* Completion card shared by all runners */
export function DoneCard({ title = "Adventure Complete!", subtitle = "You earned lots of stars!", emoji = "🎉", onAgain, backTo = "/learn", backLabel = "Back", extra }) {
  const nav = useNavigate();
  return (
    <div className="ll-bg min-h-screen pb-28 flex items-center justify-center px-4">
      <Confetti show />
      <div className="ll-card p-8 text-center max-w-md ll-pop" data-testid="done-card">
        <div className="text-6xl">{emoji}</div>
        <h2 className="font-display font-bold text-3xl text-purple-900 mt-3">{title}</h2>
        <p className="text-purple-700 mt-2">{subtitle}</p>
        {extra}
        <div className="mt-6 flex flex-col gap-3">
          <button data-testid="play-again-button" onClick={onAgain} className="ll-btn ll-btn-primary">Play again</button>
          <button data-testid="back-button-done" onClick={() => nav(backTo)} className="ll-btn ll-btn-secondary">{backLabel}</button>
        </div>
      </div>
    </div>
  );
}

export function RoundHeader({ idx, total, label }) {
  const pct = Math.round((idx / total) * 100);
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="text-sm font-bold text-purple-600">{label || `${idx + 1} / ${total}`}</div>
      <div className="flex-1 ll-progress" data-testid="quiz-progress"><span style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
